// Stimulus controller for dropdown menus with teleport to body
// Works with Turbo navigation - Stimulus auto-connects when DOM changes
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["trigger", "panel"];
  static values = {
    containerClass: { type: String, default: "" },
    noHover: { type: Boolean, default: false },
  };

  connect() {
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this);
    this.boundCloseOnEscape = this.closeOnEscape.bind(this);
  }

  disconnect() {
    this.close();
    document.removeEventListener("click", this.boundCloseOnClickOutside);
    document.removeEventListener("keydown", this.boundCloseOnEscape);
    if (this.panelElement && this.panelElement.parentNode) {
      this.panelElement.parentNode.removeChild(this.panelElement);
    }
  }

  toggle(event) {
    event.stopPropagation();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.hasTriggerTarget || !this.hasPanelTarget) return;

    this.isOpen = true;
    this.panelElement = this.panelTarget.cloneNode(true);
    this.panelElement.classList.remove("hidden");
    this.panelElement.classList.add("fixed", "z-50", "w-48", "rounded-lg", "bg-white", "shadow-lg");
    if (this.containerClassValue && typeof this.containerClassValue === "string") {
      this.containerClassValue.split(/\s+/).filter(Boolean).forEach((c) => this.panelElement.classList.add(c));
    }
    document.body.appendChild(this.panelElement);

    this.calculatePosition();

    setTimeout(() => {
      document.addEventListener("click", this.boundCloseOnClickOutside);
      document.addEventListener("keydown", this.boundCloseOnEscape);
    }, 0);
  }

  close() {
    this.isOpen = false;
    document.removeEventListener("click", this.boundCloseOnClickOutside);
    document.removeEventListener("keydown", this.boundCloseOnEscape);
    if (this.panelElement && this.panelElement.parentNode) {
      this.panelElement.parentNode.removeChild(this.panelElement);
      this.panelElement = null;
    }
  }

  calculatePosition() {
    if (!this.panelElement || !this.hasTriggerTarget) return;

    const margin = 10;
    const triggerRect = this.triggerTarget.getBoundingClientRect();
    const panelRect = this.panelElement.getBoundingClientRect();

    let top = triggerRect.bottom;
    let left = triggerRect.right - panelRect.width;

    if (left + panelRect.width > window.innerWidth) left = window.innerWidth - panelRect.width - margin;
    if (left < margin) left = margin;

    // A long panel opens on the side with the more room and scrolls inside
    // it: flipped above a trigger near the top of the page, it went off the
    // screen (export of the students, eighteen fields).
    const below = window.innerHeight - triggerRect.bottom - margin;
    const above = triggerRect.top - margin;
    if (panelRect.height > below) {
      const upward = above > below;
      const room = upward ? above : below;
      if (panelRect.height > room) {
        this.panelElement.style.maxHeight = `${room}px`;
        this.panelElement.style.overflowY = "auto";
      }
      top = upward ? triggerRect.top - Math.min(panelRect.height, room) : triggerRect.bottom;
    }

    this.panelElement.style.top = `${top}px`;
    this.panelElement.style.left = `${left}px`;
  }

  closeOnClickOutside(event) {
    if (this.element.contains(event.target)) return;
    if (this.panelElement && this.panelElement.contains(event.target)) return;
    this.close();
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close();
  }
}
